import { CalendarDate, UserId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { ContentContainer, PageHeader, Stage } from "components/layout";
import { DateRangeControl } from "components/reports/date-presets";
import { reportColors } from "components/reports/report-colors";
import { trpc } from "components/rpc/trpc";
import { defaultCardStyle } from "components/ui/card";
import { SortDown, SortUp } from "components/ui/icon";
import { MultiUserSelect } from "components/ui/multi-user-select";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Multiselect from "react-widgets/Multiselect";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useCanSeeReferralSourceOwners, useRegionId } from "store/selectors/hooks";
import { referralSourceReportSlice } from "store/slices/referral-source-report";
import { computeReferralSourceSummary } from "./referral-source-summary";

type FlatRow = { row: ContactRow; isAggregate: boolean; flatKey: string };

type RouterOutput = inferRouterOutputs<AppRouter>;
type ReportResult = RouterOutput["referralSource"]["referralSourceReport"];
type ContactRow = ReportResult["contacts"][number];
type SortField =
    | "contactName"
    | "assignedUserName"
    | "leadCount"
    | "movedCount"
    | "moveRate"
    | "activeCount"
    | "closedNoMoveCount"
    | "avgDaysToMove"
    | "totalInvoicedCents"
    | "avgInvoicePerMoveCents";

type ViewMode = "flat" | "category";

const numericSortFields: SortField[] = [
    "leadCount",
    "movedCount",
    "moveRate",
    "activeCount",
    "closedNoMoveCount",
    "avgDaysToMove",
    "totalInvoicedCents",
    "avgInvoicePerMoveCents",
];

const PAGE_SIZE = 25;
const GENERAL_FILTER_KEY = "__general";
const UNSPECIFIED_FILTER_KEY = "__unspecified";

const formatCurrency = (cents: number): string => {
    return (cents / 100).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
};

const formatPercent = (rate: number | null): string => {
    if (rate === null) return "-";
    return `${Math.round(rate * 100)}%`;
};

const ProgressBar: React.FC<{
    value: number;
    max: number;
    className: string;
}> = ({ value, max, className }) => {
    const width = max > 0 ? (value / max) * 100 : 0;
    return (
        <div className="w-full rounded-full mt-1 h-1 bg-gray-200">
            <div
                className={`rounded-full h-1 ${className}`}
                style={{ width: `${Math.min(width, 100)}%` }}
            />
        </div>
    );
};

export const ReferralSource: React.FC = () => {
    const regionId = useRegionId();
    const dispatch = useAppDispatch();
    const reportState = useAppSelector(state => state.referralSourceReport);

    const startDate = reportState.startDate;
    const endDate = reportState.endDate;
    const sortField = reportState.sortField as SortField;
    const sortAsc = reportState.sortAsc;
    const viewMode = reportState.viewMode as ViewMode;
    const categoryFilter = reportState.categoryFilter;
    const assignedUserFilter = reportState.assignedUserFilter;
    const canSeeReferralSourceOwners = useCanSeeReferralSourceOwners();

    const updateState = (partial: Partial<typeof reportState>) => {
        dispatch(referralSourceReportSlice.actions.update(partial));
    };

    const [page, setPage] = useState(0);

    const trpcClient = trpc.useTRPC();

    const reportQuery = useQuery(
        trpcClient.referralSource.referralSourceReport.queryOptions(
            {
                startDate,
                endDate,
                regionId: regionId || "",
            },
            { enabled: !!regionId },
        ),
    );

    const selectedContactCategories = useMemo(() => {
        return categoryFilter.filter(
            c => c !== GENERAL_FILTER_KEY && c !== UNSPECIFIED_FILTER_KEY,
        );
    }, [categoryFilter]);

    const allSourceRows = useMemo<ContactRow[]>(() => {
        if (!reportQuery.data) return [];
        return [...reportQuery.data.contacts, ...reportQuery.data.businessRows];
    }, [reportQuery.data]);

    const filteredContacts = useMemo(() => {
        if (!reportQuery.data) return [];
        if (categoryFilter.length > 0 && selectedContactCategories.length === 0)
            return [];
        let contacts = allSourceRows;
        if (selectedContactCategories.length > 0) {
            contacts = contacts.filter(c => {
                if (!c.organizationType) {
                    return selectedContactCategories.includes("uncategorized");
                }
                return selectedContactCategories.includes(c.organizationType);
            });
        }
        if (assignedUserFilter.length > 0) {
            contacts = contacts.filter(c =>
                assignedUserFilter.includes(c.assignedUserId ?? ""),
            );
        }
        return contacts;
    }, [
        allSourceRows,
        reportQuery.data,
        categoryFilter,
        selectedContactCategories,
        assignedUserFilter,
    ]);

    const showGeneralRows =
        (categoryFilter.length === 0 || categoryFilter.includes(GENERAL_FILTER_KEY)) &&
        assignedUserFilter.length === 0;
    const showUnspecifiedRow =
        (categoryFilter.length === 0 ||
            categoryFilter.includes(UNSPECIFIED_FILTER_KEY)) &&
        assignedUserFilter.length === 0;

    const compareRows = useCallback(
        (a: ContactRow, b: ContactRow) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            if (aVal === null && bVal === null) return 0;
            if (aVal === null) return 1;
            if (bVal === null) return -1;
            if (typeof aVal === "string" && typeof bVal === "string") {
                return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return sortAsc
                ? (aVal as number) - (bVal as number)
                : (bVal as number) - (aVal as number);
        },
        [sortField, sortAsc],
    );

    const sortedContacts = useMemo(() => {
        return [...filteredContacts].sort(compareRows);
    }, [filteredContacts, compareRows]);

    const sortedGeneralRows = useMemo(() => {
        if (!reportQuery.data) return [];
        return [...reportQuery.data.generalRows].sort(compareRows);
    }, [reportQuery.data, compareRows]);

    const paginatedContacts = useMemo(() => {
        const end = (page + 1) * PAGE_SIZE;
        return sortedContacts.slice(0, end);
    }, [sortedContacts, page]);

    const allFlatRows = useMemo((): FlatRow[] => {
        if (viewMode !== "flat") return [];
        if (!reportQuery.data) return [];
        const rows: FlatRow[] = filteredContacts.map(c => ({
            row: c,
            isAggregate: false,
            flatKey: c.contactId,
        }));
        if (showGeneralRows) {
            for (const row of reportQuery.data.generalRows) {
                rows.push({
                    row,
                    isAggregate: true,
                    flatKey: `general-${row.contactName}`,
                });
            }
        }
        if (showUnspecifiedRow && reportQuery.data.unspecifiedRow) {
            rows.push({
                row: reportQuery.data.unspecifiedRow,
                isAggregate: true,
                flatKey: "unspecified",
            });
        }
        if (assignedUserFilter.length > 0) {
            return rows.filter(r => !r.isAggregate);
        }
        return rows;
    }, [
        viewMode,
        reportQuery.data,
        filteredContacts,
        showGeneralRows,
        showUnspecifiedRow,
        assignedUserFilter,
    ]);

    const sortedFlatRows = useMemo(() => {
        return [...allFlatRows].sort((a, b) => compareRows(a.row, b.row));
    }, [allFlatRows, compareRows]);

    const paginatedFlatRows = useMemo(() => {
        const end = (page + 1) * PAGE_SIZE;
        return sortedFlatRows.slice(0, end);
    }, [sortedFlatRows, page]);

    const maxValues = useMemo(() => {
        const rows = viewMode === "flat" ? allFlatRows.map(f => f.row) : filteredContacts;
        if (rows.length === 0)
            return {
                leads: 1,
                moved: 1,
                active: 1,
                closedNoMove: 1,
                invoiced: 1,
                avgMove: 1,
            };
        return {
            leads: Math.max(...rows.map(c => c.leadCount), 1),
            moved: Math.max(...rows.map(c => c.movedCount), 1),
            active: Math.max(...rows.map(c => c.activeCount), 1),
            closedNoMove: Math.max(...rows.map(c => c.closedNoMoveCount), 1),
            invoiced: Math.max(...rows.map(c => c.totalInvoicedCents), 1),
            avgMove: Math.max(...rows.map(c => c.avgInvoicePerMoveCents || 0), 1),
        };
    }, [viewMode, allFlatRows, filteredContacts]);

    const summary = useMemo(() => {
        let rows: ContactRow[];
        if (viewMode === "flat") {
            rows = allFlatRows.map(f => f.row);
        } else {
            rows = [...filteredContacts];
            if (showGeneralRows && reportQuery.data) {
                rows.push(...reportQuery.data.generalRows);
            }
            if (showUnspecifiedRow && reportQuery.data?.unspecifiedRow) {
                rows.push(reportQuery.data.unspecifiedRow);
            }
        }
        return computeReferralSourceSummary(rows);
    }, [
        viewMode,
        allFlatRows,
        filteredContacts,
        showGeneralRows,
        showUnspecifiedRow,
        reportQuery.data,
    ]);

    const categoryOptions = useMemo(() => {
        if (!reportQuery.data) return [];
        const types = new Set<string>();
        let hasUncategorized = false;
        for (const c of allSourceRows) {
            if (c.organizationType) {
                types.add(c.organizationType);
            } else {
                hasUncategorized = true;
            }
        }
        const options: { key: string; text: string }[] = Array.from(types)
            .sort()
            .map(t => ({ key: t, text: t }));
        if (hasUncategorized)
            options.push({ key: "uncategorized", text: "Uncategorized" });
        if (reportQuery.data.generalRows.length > 0)
            options.push({ key: GENERAL_FILTER_KEY, text: "General" });
        if (reportQuery.data.unspecifiedRow)
            options.push({
                key: UNSPECIFIED_FILTER_KEY,
                text: "Unspecified",
            });
        return options;
    }, [reportQuery.data, allSourceRows]);

    const groupedByCategory = useMemo(() => {
        if (viewMode !== "category") return null;
        const groups = new Map<string, ContactRow[]>();
        for (const c of sortedContacts) {
            const key = c.organizationType || "Uncategorized";
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(c);
        }
        const entries = Array.from(groups.entries()).sort((a, b) => {
            if (a[0] === "Uncategorized") return 1;
            if (b[0] === "Uncategorized") return -1;
            if (numericSortFields.includes(sortField)) {
                const sumGroup = (rows: ContactRow[]) =>
                    rows.reduce((s, r) => s + ((r[sortField] as number) || 0), 0);
                const aSum = sumGroup(a[1]);
                const bSum = sumGroup(b[1]);
                return sortAsc ? aSum - bSum : bSum - aSum;
            }
            return a[0].localeCompare(b[0]);
        });
        return entries;
    }, [sortedContacts, viewMode, sortField, sortAsc]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            updateState({ sortAsc: !sortAsc });
        } else {
            updateState({ sortField: field, sortAsc: false });
        }
        setPage(0);
    };

    const sortIndicator = (field: SortField) => {
        if (sortField !== field) return null;
        return sortAsc ? (
            <SortUp className="inline ml-1" />
        ) : (
            <SortDown className="inline ml-1" />
        );
    };

    const renderMetricCells = (row: ContactRow) => (
        <>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">{row.leadCount}</div>
                <ProgressBar
                    value={row.leadCount}
                    max={maxValues.leads}
                    className="bg-blue-600"
                />
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">{row.movedCount}</div>
                <ProgressBar
                    value={row.movedCount}
                    max={maxValues.moved}
                    className="bg-green-600"
                />
            </td>
            <td className="py-3 px-4 text-right text-sm">
                <div className="font-medium">{formatPercent(row.moveRate)}</div>
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">{row.activeCount}</div>
                <ProgressBar
                    value={row.activeCount}
                    max={maxValues.active}
                    className="bg-gray-400"
                />
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">{row.closedNoMoveCount}</div>
                <ProgressBar
                    value={row.closedNoMoveCount}
                    max={maxValues.closedNoMove}
                    className="bg-amber-500"
                />
            </td>
            <td className="py-3 px-4 text-right text-sm">
                {row.avgDaysToMove !== null ? (
                    <span>{row.avgDaysToMove}</span>
                ) : (
                    <span className="text-gray-400">-</span>
                )}
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">
                    {row.totalInvoicedCents > 0
                        ? formatCurrency(row.totalInvoicedCents)
                        : "-"}
                </div>
                <ProgressBar
                    value={row.totalInvoicedCents}
                    max={maxValues.invoiced}
                    className="bg-purple-600"
                />
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">
                    {row.avgInvoicePerMoveCents !== null
                        ? formatCurrency(row.avgInvoicePerMoveCents)
                        : "-"}
                </div>
                {row.avgInvoicePerMoveCents !== null && (
                    <ProgressBar
                        value={row.avgInvoicePerMoveCents}
                        max={maxValues.avgMove}
                        className="bg-orange-600"
                    />
                )}
            </td>
        </>
    );

    const renderAggregateRow = (row: ContactRow, key: string) => (
        <tr key={key} className="border-b border-gray-100 hover:bg-gray-50">
            <td className="py-3 px-4">
                <span className="font-medium text-gray-700">{row.contactName}</span>
            </td>
            {canSeeReferralSourceOwners && (
                <td className="py-3 px-4">
                    <span className="text-gray-400">-</span>
                </td>
            )}
            {renderMetricCells(row)}
        </tr>
    );

    const renderTableRow = (contact: ContactRow, showOrgType = true) => {
        const isBusinessRow = !contact.contactId && !!contact.businessId;
        const linkTo = isBusinessRow
            ? `/references/organizations/show/${contact.businessId}`
            : `/references/contacts/show/${contact.contactId}/inbound`;
        const key = isBusinessRow ? `business-${contact.businessId}` : contact.contactId;
        return (
            <tr key={key} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4">
                    <Link
                        to={linkTo}
                        className="font-medium text-brand-600 hover:underline">
                        {contact.preferredName || contact.contactName}
                    </Link>
                    {!isBusinessRow && contact.companyName && (
                        <div className="text-xs text-gray-500">{contact.companyName}</div>
                    )}
                    {showOrgType && contact.organizationType && (
                        <div>
                            <span className="inline-block mt-0.5 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                {contact.organizationType}
                            </span>
                        </div>
                    )}
                </td>
                {canSeeReferralSourceOwners && (
                    <td className="py-3 px-4 text-sm text-gray-700">
                        {contact.assignedUserName || (
                            <span className="text-gray-400">-</span>
                        )}
                    </td>
                )}
                {renderMetricCells(contact)}
            </tr>
        );
    };

    const renderFooterTotals = () => {
        return (
            <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                <td className="py-3 px-4">Total ({summary.totalContacts} sources)</td>
                {canSeeReferralSourceOwners && <td className="py-3 px-4" />}
                <td className="py-3 px-4 text-right">{summary.totalLeads}</td>
                <td className="py-3 px-4 text-right">{summary.totalMoved}</td>
                <td className="py-3 px-4 text-right">
                    {formatPercent(summary.moveRate)}
                </td>
                <td className="py-3 px-4 text-right">{summary.totalActive}</td>
                <td className="py-3 px-4 text-right">{summary.totalClosedNoMove}</td>
                <td className="py-3 px-4 text-right">
                    {summary.avgDays !== null ? summary.avgDays : "-"}
                </td>
                <td className="py-3 px-4 text-right">
                    {summary.totalInvoiced > 0
                        ? formatCurrency(summary.totalInvoiced)
                        : "-"}
                </td>
                <td className="py-3 px-4" />
            </tr>
        );
    };

    const startDateValue = CalendarDate.parse(startDate).getOrElse(CalendarDate.today);
    const endDateValue = CalendarDate.parse(endDate).getOrElse(CalendarDate.today);

    return (
        <Stage>
            <PageHeader title="Referral Source Report" stackBreakpoint="md">
                <DateRangeControl
                    value={{
                        startDate: startDateValue,
                        endDate: endDateValue,
                        activePreset: reportState.activePreset,
                    }}
                    onChange={v => {
                        updateState({
                            startDate: v.startDate.toString(),
                            endDate: v.endDate.toString(),
                            activePreset: v.activePreset,
                        });
                        setPage(0);
                    }}
                />
            </PageHeader>
            <ContentContainer>
                {reportQuery.isLoading && (
                    <div className="text-center py-12 text-gray-500">
                        Loading report...
                    </div>
                )}

                {reportQuery.isError && (
                    <div className="text-center py-12 text-red-600">
                        Failed to load report. Please try again.
                    </div>
                )}

                {reportQuery.data && (
                    <>
                        <div className="pb-4 mb-4 border-b border-gray-200 space-y-3 px-8 sm:px-4 md:px-2 lg:px-0">
                            <div className="space-y-3">
                                <div className="w-full sm:w-128">
                                    <div className="mb-1">Category</div>
                                    <Multiselect
                                        data={categoryOptions}
                                        value={categoryFilter}
                                        textField="text"
                                        dataKey="key"
                                        placeholder="All categories"
                                        onChange={values => {
                                            updateState({
                                                categoryFilter: values.map(v => v.key),
                                            });
                                            setPage(0);
                                        }}
                                    />
                                </div>
                                {canSeeReferralSourceOwners && (
                                    <div className="w-full sm:w-128">
                                        <MultiUserSelect
                                            label="Assigned To"
                                            placeholder="All assignees"
                                            value={assignedUserFilter as UserId[]}
                                            onChange={userIds => {
                                                updateState({
                                                    assignedUserFilter:
                                                        userIds as string[],
                                                });
                                                setPage(0);
                                            }}
                                            regionId={regionId}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-1">
                                <SecondaryCompactButton
                                    selected={viewMode === "flat"}
                                    onClick={() => updateState({ viewMode: "flat" })}>
                                    Flat
                                </SecondaryCompactButton>
                                <SecondaryCompactButton
                                    selected={viewMode === "category"}
                                    onClick={() => updateState({ viewMode: "category" })}>
                                    By Category
                                </SecondaryCompactButton>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-gray-500 uppercase font-medium">
                                    Sources
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.totalContacts}
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div
                                    className={`text-xs ${reportColors.clients.text} uppercase font-medium`}>
                                    Total Clients
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.totalLeads}
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div
                                    className={`text-xs ${reportColors.moved.text} uppercase font-medium`}>
                                    Total Moved
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.totalMoved}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {formatPercent(summary.moveRate)} move rate
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-gray-500 uppercase font-medium">
                                    Avg Days to Move
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.avgDays !== null ? summary.avgDays : "-"}
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div
                                    className={`text-xs ${reportColors.invoiced.text} uppercase font-medium`}>
                                    Total Invoiced
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {formatCurrency(summary.totalInvoiced)}
                                </div>
                            </div>
                        </div>

                        <div className="text-sm text-gray-500 mb-2">
                            Showing{" "}
                            {viewMode === "flat"
                                ? `${Math.min(paginatedFlatRows.length, sortedFlatRows.length)} of ${sortedFlatRows.length}`
                                : `${Math.min(paginatedContacts.length, sortedContacts.length)} of ${sortedContacts.length}`}{" "}
                            sources
                            {filteredContacts.length !==
                                (reportQuery.data?.contacts.length ?? 0) &&
                                ` (filtered from ${reportQuery.data?.contacts.length})`}
                        </div>

                        <div className={`${defaultCardStyle} overflow-x-auto`}>
                            <table className="min-w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th
                                            className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort("contactName")}>
                                            Source
                                            {sortIndicator("contactName")}
                                        </th>
                                        {canSeeReferralSourceOwners && (
                                            <th
                                                className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                                onClick={() =>
                                                    handleSort("assignedUserName")
                                                }>
                                                Assigned To
                                                {sortIndicator("assignedUserName")}
                                            </th>
                                        )}
                                        <th
                                            className={`py-3 px-4 text-right text-xs font-semibold ${reportColors.clients.text} uppercase cursor-pointer hover:bg-gray-100`}
                                            onClick={() => handleSort("leadCount")}>
                                            Clients
                                            {sortIndicator("leadCount")}
                                        </th>
                                        <th
                                            className={`py-3 px-4 text-right text-xs font-semibold ${reportColors.moved.text} uppercase cursor-pointer hover:bg-gray-100`}
                                            onClick={() => handleSort("movedCount")}>
                                            Moved
                                            {sortIndicator("movedCount")}
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort("moveRate")}>
                                            Move Rate
                                            {sortIndicator("moveRate")}
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort("activeCount")}>
                                            Active
                                            {sortIndicator("activeCount")}
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-xs font-semibold text-amber-700 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() =>
                                                handleSort("closedNoMoveCount")
                                            }>
                                            Closed
                                            {sortIndicator("closedNoMoveCount")}
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort("avgDaysToMove")}>
                                            Avg Days to Move
                                            {sortIndicator("avgDaysToMove")}
                                        </th>
                                        <th
                                            className={`py-3 px-4 text-right text-xs font-semibold ${reportColors.invoiced.text} uppercase cursor-pointer hover:bg-gray-100`}
                                            onClick={() =>
                                                handleSort("totalInvoicedCents")
                                            }>
                                            Total Invoiced
                                            {sortIndicator("totalInvoicedCents")}
                                        </th>
                                        <th
                                            className={`py-3 px-4 text-right text-xs font-semibold ${reportColors.avgPerMove.text} uppercase cursor-pointer hover:bg-gray-100`}
                                            onClick={() =>
                                                handleSort("avgInvoicePerMoveCents")
                                            }>
                                            Avg/Move
                                            {sortIndicator("avgInvoicePerMoveCents")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewMode === "flat" ? (
                                        <>
                                            {paginatedFlatRows.map(f =>
                                                f.isAggregate
                                                    ? renderAggregateRow(f.row, f.flatKey)
                                                    : renderTableRow(f.row),
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {groupedByCategory?.map(
                                                ([category, contacts]) => {
                                                    const groupLeads = contacts.reduce(
                                                        (s, c) => s + c.leadCount,
                                                        0,
                                                    );
                                                    const groupMoved = contacts.reduce(
                                                        (s, c) => s + c.movedCount,
                                                        0,
                                                    );
                                                    const groupInvoiced = contacts.reduce(
                                                        (s, c) =>
                                                            s + c.totalInvoicedCents,
                                                        0,
                                                    );
                                                    const groupRate =
                                                        groupLeads > 0
                                                            ? Math.round(
                                                                  (groupMoved /
                                                                      groupLeads) *
                                                                      100,
                                                              )
                                                            : 0;
                                                    return (
                                                        <React.Fragment key={category}>
                                                            <tr className="bg-gray-100 border-b border-gray-200">
                                                                <td
                                                                    colSpan={
                                                                        canSeeReferralSourceOwners
                                                                            ? 10
                                                                            : 9
                                                                    }
                                                                    className="py-2 px-4 text-sm font-semibold text-gray-700">
                                                                    <span
                                                                        className={`inline-block w-2 h-2 rounded-full ${reportColors.categoryDot} mr-2`}
                                                                    />
                                                                    {category}{" "}
                                                                    <span className="font-normal text-gray-500">
                                                                        {contacts.length}{" "}
                                                                        sources -{" "}
                                                                        {groupLeads}{" "}
                                                                        clients -{" "}
                                                                        {groupMoved} moved
                                                                        ({groupRate}
                                                                        %) -{" "}
                                                                        {formatCurrency(
                                                                            groupInvoiced,
                                                                        )}{" "}
                                                                        invoiced
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                            {contacts.map(c =>
                                                                renderTableRow(c, false),
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                },
                                            )}
                                            {showGeneralRows &&
                                                reportQuery.data.generalRows.length > 0 &&
                                                (() => {
                                                    const generalLeads =
                                                        reportQuery.data.generalRows.reduce(
                                                            (s, r) => s + r.leadCount,
                                                            0,
                                                        );
                                                    const generalMoved =
                                                        reportQuery.data.generalRows.reduce(
                                                            (s, r) => s + r.movedCount,
                                                            0,
                                                        );
                                                    const generalRate =
                                                        generalLeads > 0
                                                            ? Math.round(
                                                                  (generalMoved /
                                                                      generalLeads) *
                                                                      100,
                                                              )
                                                            : 0;
                                                    const generalInvoiced =
                                                        reportQuery.data.generalRows.reduce(
                                                            (s, r) =>
                                                                s + r.totalInvoicedCents,
                                                            0,
                                                        );
                                                    return (
                                                        <React.Fragment key="general-group">
                                                            <tr className="bg-gray-100 border-b border-gray-200">
                                                                <td
                                                                    colSpan={
                                                                        canSeeReferralSourceOwners
                                                                            ? 10
                                                                            : 9
                                                                    }
                                                                    className="py-2 px-4 text-sm font-semibold text-gray-700">
                                                                    <span className="inline-block w-2 h-2 rounded-full bg-teal-500 mr-2" />
                                                                    General{" "}
                                                                    <span className="font-normal text-gray-500">
                                                                        {generalLeads}{" "}
                                                                        clients -{" "}
                                                                        {generalMoved}{" "}
                                                                        moved (
                                                                        {generalRate}%) -{" "}
                                                                        {formatCurrency(
                                                                            generalInvoiced,
                                                                        )}{" "}
                                                                        invoiced
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                            {sortedGeneralRows.map(row =>
                                                                renderAggregateRow(
                                                                    row,
                                                                    `general-${row.contactName}`,
                                                                ),
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })()}
                                            {showUnspecifiedRow &&
                                                reportQuery.data.unspecifiedRow &&
                                                (() => {
                                                    const u =
                                                        reportQuery.data.unspecifiedRow;
                                                    const unspecRate =
                                                        u.leadCount > 0
                                                            ? Math.round(
                                                                  (u.movedCount /
                                                                      u.leadCount) *
                                                                      100,
                                                              )
                                                            : 0;
                                                    return (
                                                        <React.Fragment key="unspecified-group">
                                                            <tr className="bg-gray-100 border-b border-gray-200">
                                                                <td
                                                                    colSpan={
                                                                        canSeeReferralSourceOwners
                                                                            ? 10
                                                                            : 9
                                                                    }
                                                                    className="py-2 px-4 text-sm font-semibold text-gray-700">
                                                                    <span className="inline-block w-2 h-2 rounded-full bg-gray-400 mr-2" />
                                                                    Unspecified{" "}
                                                                    <span className="font-normal text-gray-500">
                                                                        {u.leadCount}{" "}
                                                                        clients -{" "}
                                                                        {u.movedCount}{" "}
                                                                        moved (
                                                                        {unspecRate}
                                                                        %) -{" "}
                                                                        {formatCurrency(
                                                                            u.totalInvoicedCents,
                                                                        )}{" "}
                                                                        invoiced
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                            {renderAggregateRow(
                                                                u,
                                                                "unspecified",
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })()}
                                        </>
                                    )}
                                    {(viewMode === "flat"
                                        ? allFlatRows.length > 0
                                        : filteredContacts.length > 0) &&
                                        renderFooterTotals()}
                                </tbody>
                            </table>
                        </div>

                        {(viewMode === "flat"
                            ? sortedFlatRows.length
                            : sortedContacts.length) >
                            (page + 1) * PAGE_SIZE && (
                            <div className="text-center mt-4">
                                <SecondaryButton onClick={() => setPage(page + 1)}>
                                    Show More
                                </SecondaryButton>
                            </div>
                        )}

                        <div className="mt-4 mb-8 text-xs text-gray-400 space-y-1">
                            <p>* A client counts as moved when it has a move date set.</p>
                            <p>
                                * Avg Days to Move is calculated from client created date
                                to move date.
                            </p>
                            <p>
                                * Only sources with at least one client in the selected
                                date range are shown.
                            </p>
                        </div>
                    </>
                )}
            </ContentContainer>
        </Stage>
    );
};
